import Button from '@/components/elements/forms/buttons/Button/Button';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import { _t } from '@/locale';
import { useEffect, useRef, useState } from 'react';
import SelectAddOns from '../views/SelectAddOns';
import SelectService from '../views/SelectService';
import { EmployeeInfoContent } from './EmployeeInfoModal';
import SelectedEmployeeInfo from './SelectedEmployeeInfo';
import LegacyServiceInfoCard from './LegacyServiceInfoCard';
import type { SelectionModalInfo, SelectionModalProps, SelectionModalView } from './SelectionModal.types';

const baseTranslationKey = 'features.bookingSelection.SelectionModal' as const;

const VIEW_TITLE_KEYS = {
  recommended: `${baseTranslationKey}.recommended.title`,
  addService: `${baseTranslationKey}.addService.title`,
} as const satisfies Record<SelectionModalView, string>;

/**
 * The desktop counterpart of SelectionDrawer: the calendar's side-column add links open one
 * view directly over the summary, so there is no view stack to pop back to — the summary is
 * already on screen behind the overlay. The one exception is an (i): its detail takes over
 * this modal, reached and left by the header's back arrow, rather than opening a second one
 * on top of it.
 */
const SelectionModal = ({ view, onClose, recommended, addService, employeeInfo }: SelectionModalProps) => {
  const [info, setInfo] = useState<SelectionModalInfo | null>(null);

  // Closing the modal, or opening it on the other view, starts on the list again.
  useEffect(() => setInfo(null), [view]);

  // Looked up fresh so the detail tracks prop refreshes, and only in the list the (i) was
  // clicked in — the same id can sit in both, with a different CTA behind each.
  const listServices =
    view === 'recommended' ? recommended.services : addService.categories.flatMap((category) => category.services);
  const infoService =
    info?.kind === 'service' ? listServices.find((service) => service.id === info.serviceId) : undefined;
  const infoEmployee =
    info?.kind === 'employee' ? employeeInfo?.employees.find((employee) => employee.id === info.employeeId) : undefined;

  // The subject can vanish underneath the detail (props refresh, e.g. hydration filtering a
  // just-picked add-on); drop the dead entry so the header falls back too.
  useEffect(() => {
    if (info && !infoService && !infoEmployee) setInfo(null);
  }, [info, infoService, infoEmployee]);

  const isDetailOpen = Boolean(infoService || infoEmployee);

  // The list is hidden rather than unmounted, so its accordion and search survive backing
  // out — but display:none drops the scroll offset, so that is carried by hand.
  const listRef = useRef<HTMLDivElement>(null);
  const listScrollTop = useRef(0);
  useEffect(() => {
    if (!isDetailOpen && listRef.current) listRef.current.scrollTop = listScrollTop.current;
  }, [isDetailOpen]);

  if (!view) return null;

  const pickInfoService = () => {
    if (!infoService) return;
    const onPickService = view === 'recommended' ? recommended.onPickService : addService.onPickService;
    onPickService(infoService.id, infoService.isRecommended);
  };

  const title = infoService
    ? _t('features.bookingSelection.ServiceItem.infoTitle')
    : infoEmployee
    ? infoEmployee.name
    : _t(VIEW_TITLE_KEYS[view]);

  const body = (() => {
    switch (view) {
      case 'recommended':
        return (
          <SelectAddOns {...recommended} onOpenServiceInfo={(serviceId) => setInfo({ kind: 'service', serviceId })} />
        );
      case 'addService':
        return (
          <div className="gap-xl flex flex-col">
            {/* Above the list, not in SelectService's own employeePanel slot: at
                `isNarrow={false}` that slot is a 320px right column, which a 560px card
                cannot hold. */}
            {employeeInfo && (
              <SelectedEmployeeInfo
                {...employeeInfo}
                onOpenInfo={(employeeId) => setInfo({ kind: 'employee', employeeId })}
              />
            )}
            {/* `isNarrow={false}` is what renders the "N tjänster" category count. */}
            <SelectService
              {...addService}
              isNarrow={false}
              hideHeader
              ctaAction="add"
              onOpenServiceInfo={(serviceId) => setInfo({ kind: 'service', serviceId })}
            />
          </div>
        );
      default: {
        const never: never = view;
        console.error('Invalid SelectionModal view', never);
        return null;
      }
    }
  })();

  const detail = infoService ? (
    <div className="gap-md flex flex-col">
      <LegacyServiceInfoCard service={infoService} />
      <Button block variant="primary" size="md" onClick={pickInfoService}>
        {_t('features.bookingSelection.ServiceItem.infoCta.add')}
      </Button>
    </div>
  ) : infoEmployee ? (
    <EmployeeInfoContent employee={infoEmployee} />
  ) : null;

  return (
    <Modal isOpen onRequestClose={onClose}>
      <Modal.Content newDesign floating>
        <Modal.Header
          newDesign
          floating
          title={title}
          onClose={onClose}
          onBack={info ? () => setInfo(null) : undefined}
          backLabel={_t('back')}
        />
        {/* `rounded-b-lg`: the card itself is rounded but not clipping, so the scroll container
            has to carry the bottom corners. Hidden rather than unmounted while the detail is
            up — the card then takes the detail's own height instead of the list's. */}
        <div
          ref={listRef}
          hidden={isDetailOpen}
          onScroll={(event) => (listScrollTop.current = event.currentTarget.scrollTop)}
          className="bg-surface_mp px-md py-md max-h-[70vh] overflow-y-auto rounded-b-lg">
          {body}
        </div>
        {detail && <div className="bg-surface_mp px-md py-md max-h-[70vh] overflow-y-auto rounded-b-lg">{detail}</div>}
      </Modal.Content>
    </Modal>
  );
};

export default SelectionModal;
